import {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button, Box, Typography,
} from '@material-ui/core';
import { lookupFieldAction } from 'services/templateFieldActions';
import { createFieldActionExecutor } from 'utils/templateFieldActionExecutor';
import { pickValue } from 'utils/objects';

function TemplateFieldActionsBridge({ template, valuePath, onOptions, onValue, onState }) {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'views.people_types_edit.field_modal.field_actions',
  });
  const [failed, setFailed] = useState({});
  const [busy, setBusy] = useState({});
  const current = useRef({});
  current.current = {
    template,
    valuePath,
    onOptions,
    onValue,
    onState,
    setFieldValue,
  };
  const executor = useRef();

  useEffect(() => {
    const runtime = createFieldActionExecutor({
      getTemplate: () => current.current.template,
      lookup: lookupFieldAction,
      onOptions: (name, state) => current.current.onOptions(name, state),
      onValue: (name, value) => {
        const path = current.current.valuePath;
        current.current.setFieldValue(path ? `${path}.${name}` : name, value);
        current.current.onValue?.({
          name,
          value,
        });
      },
      onState: (field, state) => {
        const label = field.displayname || field.fieldname;
        setBusy((previous) => {
          const next = {
            ...previous,
          };
          if (state.loading) next[field.fieldname] = label;
          else delete next[field.fieldname];
          return next;
        });
        current.current.onState?.(field.fieldname, {
          loading: state.loading,
          error: state.error ? t('runtime_error', {
            field: label,
          }) : undefined,
        });
        setFailed((previous) => {
          const next = {
            ...previous,
          };
          if (state.error) next[field.fieldname] = label;
          else delete next[field.fieldname];
          return next;
        });
      },
    });
    executor.current = runtime;
    return () => runtime.dispose();
  }, [valuePath]);

  useEffect(() => {
    executor.current.observe(valuePath ? pickValue(values, valuePath) || {} : values);
  }, [values, valuePath, template]);

  return <>
    {Object.entries(busy).map(([name, label]) => <Typography key={name} variant="body2" color="textSecondary" role="status">
      {t('runtime_loading', {
        field: label,
      })}
    </Typography>)}
    {Object.entries(failed).map(([name, label]) => (
      <Box key={name} mt={1}>
        <Typography variant="body2" color="error">{t('runtime_error', {
          field: label,
        })}</Typography>
        <Button size="small" color="primary" onClick={() => {
          setFailed((previous) => { const next = {
            ...previous,
          }; delete next[name]; return next; });
          executor.current.retry(name);
        }}>{t('retry')}</Button>
      </Box>
    ))}
  </>;
}

TemplateFieldActionsBridge.propTypes = {
  template: PropTypes.array.isRequired,
  valuePath: PropTypes.string.isRequired,
  onOptions: PropTypes.func.isRequired,
  onValue: PropTypes.func,
  onState: PropTypes.func.isRequired,
};
TemplateFieldActionsBridge.defaultProps = {
  onValue: undefined,
};
export default TemplateFieldActionsBridge;
